import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Clock, Info } from 'lucide-react';
import { differenceInDays } from 'date-fns';
import { Tables } from '@/integrations/supabase/types';

type Lead = Tables<'leads'>;

interface StageVelocityChartProps {
  leads: Lead[];
  events?: Array<{
    action_type: string;
    created_at: string;
    lead_id: string | null;
    meta?: { from_stage?: string; to_stage?: string } | null;
  }>;
}

interface VelocityData {
  stage: string;
  avgDays: number;
  count: number;
}

export function StageVelocityChart({ leads, events }: StageVelocityChartProps) {
  const velocityData = useMemo(() => {
    try {
      const stages = ['new', 'contacted', 'appointment', 'proposal'];
      const now = new Date();
      
      // First, try to use stage change events if available
      const stageChanges = events?.filter(e => e.action_type === 'stage_change' && e.meta) || [];
      
      if (stageChanges.length > 5) {
        // We have stage history data - compute actual velocity
        const stageTimings: Record<string, number[]> = {};
        
        stageChanges.forEach(event => {
          const meta = event.meta as { from_stage?: string; to_stage?: string };
          if (meta?.from_stage && meta?.to_stage) {
            // Find previous stage change for this lead
            const prevEvent = stageChanges.find(
              e => e.lead_id === event.lead_id && 
              (e.meta as { to_stage?: string })?.to_stage === meta.from_stage &&
              new Date(e.created_at) < new Date(event.created_at)
            );
            
            if (prevEvent) {
              const daysInStage = differenceInDays(
                new Date(event.created_at),
                new Date(prevEvent.created_at)
              );
              
              if (daysInStage >= 0 && daysInStage < 365) {
                if (!stageTimings[meta.from_stage]) stageTimings[meta.from_stage] = [];
                stageTimings[meta.from_stage].push(daysInStage);
              }
            }
          }
        });

        const result: VelocityData[] = stages.map(stage => {
          const timings = stageTimings[stage] || [];
          const avgDays = timings.length > 0 
            ? Math.round(timings.reduce((a, b) => a + b, 0) / timings.length)
            : 0;
          return {
            stage: stage.charAt(0).toUpperCase() + stage.slice(1),
            avgDays,
            count: timings.length,
          };
        });

        return result;
      }

      // Fallback: Calculate approximate velocity based on current stage and created_at
      const result: VelocityData[] = stages.map(stage => {
        const stageLeads = leads.filter(l => l.pipeline_stage === stage);
        if (stageLeads.length === 0) {
          return { stage: stage.charAt(0).toUpperCase() + stage.slice(1), avgDays: 0, count: 0 };
        }
        
        const totalDays = stageLeads.reduce((sum, l) => {
          const created = new Date(l.created_at);
          const lastActivity = l.last_interaction_at ? new Date(l.last_interaction_at) : now;
          // Use the earlier of last_interaction_at or now
          const endDate = lastActivity < now ? lastActivity : now;
          return sum + Math.max(0, differenceInDays(endDate, created));
        }, 0);
        
        return {
          stage: stage.charAt(0).toUpperCase() + stage.slice(1),
          avgDays: Math.round(totalDays / stageLeads.length),
          count: stageLeads.length,
        };
      });

      return result;
    } catch (err) {
      console.error('INSIGHTS_STAGE_VELOCITY_ERROR', err);
      return [];
    }
  }, [leads, events]);

  const hasData = velocityData.some(d => d.avgDays > 0 || d.count > 0);
  const hasStageHistory = (events?.filter(e => e.action_type === 'stage_change').length || 0) > 5;

  if (!hasData) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Stage Velocity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[200px] flex flex-col items-center justify-center text-center px-4">
            <Clock className="w-10 h-10 text-muted-foreground/30 mb-3" />
            <p className="text-sm font-medium text-muted-foreground mb-1">
              No velocity data yet
            </p>
            <p className="text-xs text-muted-foreground/70 max-w-[280px]">
              {hasStageHistory 
                ? 'Move leads through Revenue Flow stages to see how long they spend in each stage.'
                : 'Stage velocity tracks time spent in each Revenue Flow stage. Add leads and move them through stages to generate data.'}
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base font-medium">Stage Velocity</CardTitle>
          {!hasStageHistory && (
            <div className="flex items-center gap-1 text-xs text-muted-foreground">
              <Info className="w-3 h-3" />
              <span>Estimated</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={velocityData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" vertical={false} />
            <XAxis dataKey="stage" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} width={30} />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value: number, name: string, props: { payload: VelocityData }) => [
                `${value} days (${props.payload.count} leads)`,
                'Avg Time',
              ]}
            />
            <Bar dataKey="avgDays" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
